import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { ApiError, NetworkError } from '../api/client.js';
import type { ImportJob, ImportOutcome, ImportResult } from '../api/types.js';

/**
 * Importing a subscription list, and the report afterwards (M2.6.3).
 *
 * **The report is the feature.** M2.2 can already import 640 feeds; what
 * decides whether somebody trusts this product with their reading life is
 * whether they can tell what happened to each one. A progress bar that ends
 * in "done" would be worse than useless for a list where fifteen feeds have
 * quietly died since 2022 — and in a genuine export we measured, fifteen of
 * eighty-two had.
 *
 * ## It leads with what worked
 *
 * A list of eighty-two rows with fifteen marked in red reads as a failure. The
 * same information as "67 feeds added" followed by "15 need a look" reads as
 * what it is: a successful migration with some dead links to tidy. The
 * grouping is the whole difference, and it costs nothing.
 *
 * ## "Unreachable" is not a verdict, so it offers a retry
 *
 * A feed can be unreachable because it is gone, because the publisher was
 * down for a minute, or because we were too polite to fetch it in time. Only
 * the first is permanent, and the user cannot tell the three apart — so the
 * failures group has a button that tries them again through the ordinary
 * subscribe path, one at a time.
 *
 * ## Polling, and why it stops
 *
 * A small import comes back finished and never polls at all. A larger one is
 * polled every two seconds until it completes — with a ceiling, because a job
 * that stops advancing must not leave a browser tab asking about it for ever.
 */

/** How often to ask for progress. */
const POLL_INTERVAL_MS = 2_000;

/**
 * How long to keep asking.
 *
 * Ten minutes is comfortably longer than the deployed pipeline takes for 640
 * feeds and short enough that an abandoned tab stops. Reaching it is not an
 * error — the report is still there, and the page says how to come back to it.
 */
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** The largest file worth sending, matching the API's own limit. */
const MAX_FILE_BYTES = 2 * 1024 * 1024;

export interface ImportScreenProps {
  readonly onUpload: (opml: string) => Promise<ImportJob>;
  /**
   * Fetches the account's own subscriptions as OPML (M2.3.1).
   *
   * Offered on the same screen as import, because the two are one idea: this
   * is where somebody comes to move a subscription list, and the direction
   * they are moving it is their business. Putting the export somewhere else
   * would make leaving feel like a thing to be found.
   */
  readonly onExport?: (() => Promise<string>) | undefined;
  readonly onPoll: (jobId: string) => Promise<ImportJob>;
  /** Retries one feed through the ordinary subscribe path. */
  readonly onRetry: (url: string) => Promise<void>;
  /** Called when the screen closes, so the sidebar can catch up. */
  readonly onClose: (imported: boolean) => void;
}

/** Outcomes that mean the feed is in the reader now. */
const SUCCEEDED: readonly ImportOutcome[] = ['imported', 'redirected'];

export function ImportScreen(props: ImportScreenProps): ReactNode {
  const [job, setJob] = useState<ImportJob | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [retrying, setRetrying] = useState<ReadonlySet<string>>(new Set());
  const [retried, setRetried] = useState<ReadonlySet<string>>(new Set());
  const [gaveUpPolling, setGaveUpPolling] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const anythingImported =
    job !== undefined &&
    job.results.some((result) => SUCCEEDED.includes(result.outcome)) === true;

  /* ------------------------------------------------------------ polling -- */

  const running = job !== undefined && job.status === 'running';
  const jobId = job?.jobId;

  useEffect(() => {
    if (!running || jobId === undefined) {
      return undefined;
    }

    let cancelled = false;
    const startedAt = Date.now();

    const tick = async (): Promise<void> => {
      if (cancelled) {
        return;
      }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setGaveUpPolling(true);
        return;
      }

      try {
        const next = await props.onPoll(jobId);
        if (!cancelled) {
          setJob(next);
        }
      } catch {
        /*
         * A failed poll is not a failed import. The work is happening on the
         * server either way, so the next tick simply asks again rather than
         * replacing the report with an error.
         */
      }

      if (!cancelled) {
        timer = setTimeout(() => {
          void tick();
        }, POLL_INTERVAL_MS);
      }
    };

    let timer = setTimeout(() => {
      void tick();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [running, jobId, props]);

  /* ------------------------------------------------------------- upload -- */

  const upload = useCallback(
    async (file: File): Promise<void> => {
      setError(undefined);

      if (file.size > MAX_FILE_BYTES) {
        setError(
          `That file is ${String(Math.round(file.size / 1024))} KB, and the limit is ` +
            `${String(MAX_FILE_BYTES / 1024)} KB. A list of 2,000 feeds is about 400 KB.`,
        );
        return;
      }

      setBusy(true);
      try {
        const text = await file.text();
        setJob(await props.onUpload(text));
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught.userMessage
            : caught instanceof NetworkError
              ? 'The service could not be reached. Try again in a moment.'
              : 'That file could not be read.',
        );
      } finally {
        setBusy(false);
      }
    },
    [props],
  );

  /* -------------------------------------------------------------- retry -- */

  const retry = async (result: ImportResult): Promise<void> => {
    setRetrying((current) => new Set(current).add(result.url));
    try {
      await props.onRetry(result.url);
      setRetried((current) => new Set(current).add(result.url));
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.userMessage : 'That feed could not be added.',
      );
    } finally {
      setRetrying((current) => {
        const next = new Set(current);
        next.delete(result.url);
        return next;
      });
    }
  };

  /* ------------------------------------------------------------- render -- */

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Import subscriptions"
    >
      <div className="overlay__card overlay__card--wide">
        <h2 className="overlay__title">Import your subscriptions</h2>

        {job === undefined ? (
          <>
            <p className="overlay__text">
              Most readers can export your list as an OPML file — often under Settings,
              sometimes called “export subscriptions”. Choose it here and every feed comes
              across, folders and all.
            </p>

            <label className="field">
              <span className="field__label">Subscription list</span>
              <input
                className="field__input"
                ref={fileInput}
                type="file"
                accept=".opml,.xml,text/xml,application/xml,text/x-opml"
                disabled={busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file !== undefined) {
                    void upload(file);
                  }
                }}
              />
            </label>

            {busy && (
              <p className="overlay__text" role="status">
                Reading the file and fetching each feed. A long list takes a few minutes.
              </p>
            )}

            {error !== undefined && (
              <p className="auth__error" role="alert">
                {error}
              </p>
            )}

            <div className="overlay__actions">
              <button
                className="button"
                type="button"
                disabled={busy}
                onClick={() => {
                  props.onClose(false);
                }}
              >
                Cancel
              </button>
              {props.onExport !== undefined && <ExportButton onExport={props.onExport} />}
            </div>
          </>
        ) : (
          <Report
            job={job}
            error={error}
            retrying={retrying}
            retried={retried}
            gaveUpPolling={gaveUpPolling}
            onRetry={(result) => {
              void retry(result);
            }}
            onClose={() => {
              props.onClose(anythingImported || retried.size > 0);
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Taking the subscription list out again (M2.3.1).
 *
 * The file is fetched on click rather than held ready, because an export of
 * 640 feeds is a request nobody has asked for until they ask for it — and a
 * link built on mount would be stale the moment somebody added a feed.
 *
 * A blob URL, revoked after the click. The alternative — pointing a link
 * straight at the endpoint — cannot work: the export needs an `Authorization`
 * header, and a browser navigation sends none.
 */
function ExportButton({
  onExport,
}: {
  readonly onExport: () => Promise<string>;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <>
      <button
        className="button"
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(undefined);

          onExport()
            .then((opml) => {
              let url: string;
              try {
                url = URL.createObjectURL(new Blob([opml], { type: 'text/x-opml+xml' }));
              } catch {
                setError('This browser would not let us build the file.');
                return;
              }

              const link = document.createElement('a');
              link.href = url;
              link.download = `feedreader-subscriptions-${new Date()
                .toISOString()
                .slice(0, 10)}.opml`;
              link.click();

              // Released immediately: the click has already taken the data.
              URL.revokeObjectURL(url);
            })
            .catch((caught: unknown) => {
              setError(
                caught instanceof ApiError
                  ? caught.userMessage
                  : 'The export could not be produced.',
              );
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        {busy ? 'Preparing…' : 'Export my subscriptions'}
      </button>
      {error !== undefined && (
        <p className="auth__error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

/* ------------------------------------------------------------- the report -- */

function Report(props: {
  readonly job: ImportJob;
  readonly error: string | undefined;
  readonly retrying: ReadonlySet<string>;
  readonly retried: ReadonlySet<string>;
  readonly gaveUpPolling: boolean;
  readonly onRetry: (result: ImportResult) => void;
  readonly onClose: () => void;
}): ReactNode {
  const { job } = props;

  const added = job.results.filter((result) => SUCCEEDED.includes(result.outcome));
  const already = job.results.filter((result) => result.outcome === 'alreadySubscribed');
  const failed = job.results.filter((result) => result.outcome === 'unreachable');
  const refused = job.results.filter((result) => result.outcome === 'quotaExceeded');
  const waiting = job.results.filter((result) => result.outcome === 'pending');

  const done = job.status === 'completed';

  return (
    <>
      {/*
       * The headline is what worked. The same numbers led by the failures
       * read as a broken import rather than a finished one.
       */}
      <p className="import__headline">
        {done
          ? `${String(added.length)} ${added.length === 1 ? 'feed' : 'feeds'} added`
          : `Adding your feeds — ${String(job.attempted)} of ${String(job.total)}`}
        {job.foldersCreated > 0 &&
          `, in ${String(job.foldersCreated)} ${job.foldersCreated === 1 ? 'folder' : 'folders'}`}
        .
      </p>

      {!done && (
        <>
          <progress
            className="import__progress"
            max={job.total}
            value={job.attempted}
            aria-label="Import progress"
          />
          <p className="overlay__text" role="status">
            {props.gaveUpPolling
              ? 'Still working. This is taking longer than usual — the import carries on ' +
                'without this page, and closing it is safe.'
              : 'You can close this and carry on reading; the rest keep arriving.'}
          </p>
        </>
      )}

      <Group
        title={`Added (${String(added.length)})`}
        results={added}
        emptyNote={done ? 'Nothing new — see below.' : undefined}
        renderNote={(result) =>
          result.outcome === 'redirected' && result.resolvedUrl !== null
            ? `moved to ${result.resolvedUrl}`
            : undefined
        }
      />

      {already.length > 0 && (
        <Group
          title={`Already subscribed (${String(already.length)})`}
          results={already}
        />
      )}

      {refused.length > 0 && (
        <Group
          title={`Over your feed limit (${String(refused.length)})`}
          results={refused}
          note="Your account is at its feed limit. Remove a few feeds and import again — nothing here was lost."
          renderNote={(result) => result.detail ?? undefined}
        />
      )}

      {failed.length > 0 && (
        <Group
          title={`These need a look (${String(failed.length)})`}
          results={failed}
          note={
            'We could not fetch these. Feeds move and shut down, and an older list ' +
            'usually has a few — but a publisher having a bad minute looks the same ' +
            'from here, so it is worth trying again.'
          }
          renderNote={(result) => result.detail ?? undefined}
          onRetry={props.onRetry}
          retrying={props.retrying}
          retried={props.retried}
        />
      )}

      {waiting.length > 0 && (
        <Group title={`Still going (${String(waiting.length)})`} results={waiting} />
      )}

      {props.error !== undefined && (
        <p className="auth__error" role="alert">
          {props.error}
        </p>
      )}

      <div className="overlay__actions">
        <button className="button button--primary" type="button" onClick={props.onClose}>
          {done ? 'Done' : 'Close and keep going'}
        </button>
        <DownloadReport job={job} />
      </div>
    </>
  );
}

function Group(props: {
  readonly title: string;
  readonly results: readonly ImportResult[];
  readonly note?: string | undefined;
  readonly emptyNote?: string | undefined;
  readonly renderNote?: ((result: ImportResult) => string | undefined) | undefined;
  readonly onRetry?: ((result: ImportResult) => void) | undefined;
  readonly retrying?: ReadonlySet<string> | undefined;
  readonly retried?: ReadonlySet<string> | undefined;
}): ReactNode {
  if (props.results.length === 0 && props.emptyNote === undefined) {
    return null;
  }

  return (
    <section className="import__group">
      <h3 className="import__group-title">{props.title}</h3>
      {props.note !== undefined && <p className="import__group-note">{props.note}</p>}

      {props.results.length === 0 ? (
        <p className="import__group-note">{props.emptyNote}</p>
      ) : (
        <ul className="import__list">
          {props.results.map((result) => {
            const note = props.renderNote?.(result);
            const isRetrying = props.retrying?.has(result.url) === true;
            const isRetried = props.retried?.has(result.url) === true;

            return (
              <li className="import__row" key={`${String(result.index)}-${result.url}`}>
                <span className="import__feed">
                  {/*
                   * The name from their own file, falling back to the address.
                   * A report of 640 rows of URLs is not something anybody can
                   * read down.
                   */}
                  <span className="import__feed-title">{result.title ?? result.url}</span>
                  {result.title !== null && (
                    <span className="import__feed-url">{result.url}</span>
                  )}
                  {note !== undefined && (
                    <span className="import__feed-note">{note}</span>
                  )}
                </span>

                {props.onRetry !== undefined &&
                  (isRetried ? (
                    <span className="import__retried">added</span>
                  ) : (
                    <button
                      className="button button--small"
                      type="button"
                      disabled={isRetrying}
                      onClick={() => {
                        props.onRetry?.(result);
                      }}
                    >
                      {isRetrying ? 'Trying…' : 'Try again'}
                    </button>
                  ))}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/**
 * The report as a file (M2.6.3's third criterion).
 *
 * CSV, because the person this matters to has 640 feeds and will want to open
 * it in something — and because a downloaded file is the only form of this
 * report that survives closing the tab.
 *
 * The object URL is built when the button is rendered and revoked when it goes
 * away; a blob held for the life of the page is a leak, and a report of 640
 * rows is not small.
 *
 * **It renders nothing rather than failing** when the browser will not make an
 * object URL. Found by a test: an exception here unmounted the entire report,
 * so a viewer with blob URLs blocked would have lost all 82 rows to a missing
 * download button. The report is the feature; the link is a convenience.
 */
function DownloadReport({ job }: { job: ImportJob }): ReactNode {
  const [href, setHref] = useState<string | undefined>(undefined);

  useEffect(() => {
    const csv = [
      ['outcome', 'title', 'url', 'resolvedUrl', 'detail'].join(','),
      ...job.results.map((result) =>
        [
          result.outcome,
          result.title ?? '',
          result.url,
          result.resolvedUrl ?? '',
          result.detail ?? '',
        ]
          .map(csvCell)
          .join(','),
      ),
    ].join('\n');

    let url: string;
    try {
      url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    } catch {
      return undefined;
    }

    setHref(url);

    return () => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // Nothing to release, which is not a problem worth raising.
      }
    };
  }, [job]);

  if (href === undefined) {
    return null;
  }

  return (
    <a className="button" href={href} download={`import-${job.jobId}.csv`}>
      Download the report
    </a>
  );
}

/** RFC 4180: quote anything containing a comma, a quote or a newline. */
function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}
